import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import routes from '@/router/routes';
import { useUserStore } from '@/store/useUserStore';
import { Popover, Avatar } from 'antd';
import { logout } from '@/apis/login';
import { useNavigate } from 'react-router-dom';
import { HomeFilled } from '@ant-design/icons';
import './index.scss';

const Header = () => {
  const location = useLocation();
  const { userInfo, clearAuth } = useUserStore();

  const currentBreadcrumb = useMemo(() => {
    const root = routes.find((r) => Array.isArray(r.children));
    const children = root?.children || [];
    const pathname = location.pathname.replace(/^\/+/, '');

    // 匹配最长路径的子路由，支持隐藏路由
    const matched = children
      .filter((r) => typeof r.path === 'string')
      .filter((r) => pathname.startsWith(r.path))
      .sort((a, b) => (b.path?.length || 0) - (a.path?.length || 0))[0];

    // 如果是detail页面且有parentPath，需要构建层级面包屑
    if (matched?.parentPath) {
      const parentRoute = children.find((r) => r.path === matched.parentPath);
      const parentTitle = parentRoute?.title || matched.parentPath;
      const parentPath = parentRoute?.path || matched.parentPath;
      const currentTitle = matched.title || 'Detail';

      // 检查父路由是否有 groupKey
      if (parentRoute?.groupKey) {
        const gk = parentRoute.groupKey;
        const groupTitle = gk.charAt(0).toUpperCase() + gk.slice(1);

        return {
          groupTitle,
          parentTitle,
          currentTitle,
          parentPath,
          isNested: true,
          hasGroup: true,
        };
      }

      return {
        parentTitle,
        currentTitle,
        parentPath,
        isNested: true,
        hasGroup: false,
      };
    }

    // 检查当前路由是否有 groupKey（表示它属于一个分组）
    if (matched?.groupKey) {
      const gk = matched.groupKey;
      const groupTitle = gk.charAt(0).toUpperCase() + gk.slice(1);
      const currentTitle = matched.title || 'Page';

      return {
        groupTitle,
        currentTitle,
        isNested: true,
        hasGroup: true,
        isGroupChild: true,
      };
    }

    // 普通页面
    const title =
      matched?.title ||
      (matched?.index
        ? 'Dashboard'
        : matched?.path
          ? matched.path
              .split('/')
              .filter(Boolean)
              .map((seg) => seg.charAt(0).toUpperCase() + seg.slice(1))
              .join(' ')
          : 'Dashboard');

    return {
      currentTitle: title,
      isNested: false,
      hasGroup: false,
    };
  }, [location.pathname]);

  const goParent = () => {
    navigate(currentBreadcrumb.parentPath);
  };

  const userName = userInfo?.username || 'user';

  const navigate = useNavigate();
  const handleLogout = () => {
    logout().then(() => {
      clearAuth();
      navigate('/login');
    });
  };

  return (
    <header className="appHeader">
      <div className="breadcrumb">
        <HomeFilled style={{ color: '#8d8d8d', fontSize: 14 }} />
        <span className="crumbText">
          DYF /{' '}
          {currentBreadcrumb.hasGroup && currentBreadcrumb.isGroupChild ? (
            // 有分组的子路由：DYF / Videos / System Videos
            <>
              <span className="crumbParent">{currentBreadcrumb.groupTitle}</span> / <span className="crumbTitle">{currentBreadcrumb.currentTitle}</span>
            </>
          ) : currentBreadcrumb.hasGroup && currentBreadcrumb.isNested ? (
            // 有分组的 detail 页面：DYF / Videos / System Videos / Detail
            <>
              <span className="crumbParent">{currentBreadcrumb.groupTitle}</span> /{' '}
              <span style={{ cursor: 'pointer' }} onClick={goParent}>
                {currentBreadcrumb.parentTitle}
              </span>{' '}
              / <span className="crumbTitle">{currentBreadcrumb.currentTitle}</span>
            </>
          ) : currentBreadcrumb.isNested ? (
            // 没有分组的 detail 页面：DYF / Training processes / Detail
            <>
              <span style={{ cursor: 'pointer' }} onClick={goParent}>
                {currentBreadcrumb.parentTitle}
              </span>{' '}
              / <span className="crumbTitle">{currentBreadcrumb.currentTitle}</span>
            </>
          ) : (
            // 普通一级页面：DYF / Training processes
            <span className="crumbTitle">{currentBreadcrumb.currentTitle}</span>
          )}
        </span>
      </div>
      <div className="userInfo">
        <Popover
          content={
            <div className="userInfoPopover">
              <div className="userInfoPopoverItem">
                <span onClick={handleLogout}>Logout</span>
              </div>
            </div>
          }
        >
          <span className="userName">{userName}</span>
          {userInfo?.avatar ? <Avatar src={userInfo?.avatar} size={32} /> : <Avatar size={32}>{userName.charAt(0)}</Avatar>}
        </Popover>
      </div>
    </header>
  );
};

export default Header;
